import {
  Sort,
  TableOld,
  TableToolbar,
  TableWrapper,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeFilter, FilterButton, Filters } from 'components/filters';
import { Currency } from 'types/general';
import TextFilter from 'components/filters/TextFilter';
import DownloadExcelButton from 'components/table/DownloadExcelButton/DownloadExcelButton';
import { useParentCompany } from 'state/ParentCompany';
import { useRecognitionRecipients } from 'state/Recognition';
import { DateRange } from 'types/Common';
import { currencyFormatter } from 'utils/Currency';
import { usePagination } from 'utils/Pagination';
import { usePointsLabel } from 'containers/Recognition/common/usePointsLabel';
import GenerateTopRecipientsNewsButton from './GenerateTopRecipientsNewsButton/GenerateTopRecipientsNewsButton';
import RecipientSendersDetails from './RecipientSendersDetails';

interface RecipientsFilters {
  employeeName?: string;
  recognitionDateRange?: DateRange;
}

export const RecognitionsRecipientsList = () => {
  const { t } = useTranslation();

  const [sort, setSort] = useState<Sort>(null);
  const [filters, setFilters] = useState<RecipientsFilters>({});
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const recipients = useRecognitionRecipients({
    page,
    pageSize,
    employeeName: filters.employeeName,
    dateFrom: filters.recognitionDateRange?.from,
    dateTo: filters.recognitionDateRange?.to,
    sort: sort
      ? `${sort.order === 'desc' ? '-' : ''}${sort.columnKey}`
      : undefined,
  });

  const pointsLabel = usePointsLabel();

  const { parentCompany } = useParentCompany();
  const accountCurrency = parentCompany?.defaultCurrency as Currency;

  const handleFilterChange = (newFilters: RecipientsFilters) => {
    setPage(1);
    setFilters(newFilters);
  };

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack direction="row" justifyContent="end" p="10px" gap="10px">
          <Filters value={filters} onChange={handleFilterChange}>
            {({ value, setFilter }) => (
              <>
                <FilterButton
                  label={t('recognition.recognitionDateRangeFilter')}
                  value={value.recognitionDateRange}
                  onChange={(newValue) =>
                    setFilter('recognitionDateRange', newValue)
                  }
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <DateRangeFilter
                      value={value}
                      onApply={applyFilter}
                      onClear={clearFilter}
                      onCancel={close}
                      suggestions={['week', 'month', 'year']}
                      withTimezone
                    />
                  )}
                </FilterButton>
                <TextFilter
                  value={value.employeeName}
                  onApplyFilter={(newValue) =>
                    setFilter('employeeName', newValue)
                  }
                  hint={t('recognition.recipientsListFiltersSearchHint')}
                />
              </>
            )}
          </Filters>
          <Divider orientation="vertical" variant="middle" flexItem />
          <DownloadExcelButton
            listLength={recipients.data?.count ?? 0}
            loading={recipients.xlsExport.exporting}
            exportToExcel={recipients.xlsExport.exportToXLS}
          />
          <Divider orientation="vertical" variant="middle" flexItem />
          <GenerateTopRecipientsNewsButton />
        </Stack>
      </TableToolbar>
      <TableOld
        data={recipients.data?.employees}
        loading={recipients.loading}
        rowKey="id"
        columns={[
          {
            key: 'name',
            title: t('entity_type.employee'),
            render: (_, { firstName, lastName }) =>
              [firstName, lastName].join(' '),
          },
          {
            key: 'points',
            title: t('recognition.recipientsListTotalReceived', {
              recognitionCurrency: pointsLabel,
            }),
            sort: true,
            align: 'right',
          },
          {
            key: 'currencyValue',
            title: t('recognition.recipientsListTotalReceivedCurrency', {
              accountCurrency,
            }),
            sort: true,
            render: (value: number) =>
              currencyFormatter.formatFractionalUnitAmount(
                value,
                accountCurrency,
              ),
            align: 'right',
          },
          {
            key: 'count',
            title: t('recognition.recipientsListRecognitionCount'),
            sort: true,
            align: 'right',
          },
          {
            key: 'sendersDetails',
            title: t('recognition.recipientsListSendersDetails'),
            render: (_, record) => (
              <RecipientSendersDetails
                employeeId={record.id}
                recognitionDateRange={filters.recognitionDateRange}
              />
            ),
          },
          {
            key: 'lastAwardedAt',
            title: t('recognition.recipientsListLastActivity'),
            whiteSpace: 'nowrap',
            render: (value?: string) =>
              value ? moment(value).format('l') : '-',
          },
        ]}
        sort={sort}
        onSortChange={setSort}
        pagination={
          recipients.data
            ? {
                count: recipients.data.count,
                page: recipients.data.page,
                pageSize: recipients.data.pageSize,
                onChange: (page, pageSize) => {
                  setPage(page);
                  setPageSize(pageSize);
                },
              }
            : undefined
        }
      />
    </TableWrapper>
  );
};
